import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import "../../i18n";
import { CompanyChip, Coverage, MethodChip, Notice, StatusPill } from "../../design/components";
import { METHODS } from "../../design/methods";
import { HEALTH_PILL, METHOD_BY_CONNECTION, STATUS_LABEL } from "../../domain/health";
import { useEntities } from "../../api/queries";
import { relative } from "../../lib/format";
import type { EntitySummary } from "../../api/types";
import { CompaniesFilters, type CompanyFilters } from "./CompaniesFilters";
import { CopyButton } from "./CopyButton";
import styles from "./CompaniesList.module.css";

const FILTER_KEYS = ["q", "status", "health", "method", "wave"] as const;
type SortKey = "legalName" | "status" | "health" | "lastDocumentSeenAt";
type SortState = { key: SortKey; direction: "asc" | "desc" };

const DEFAULT_SORT: SortState = { key: "legalName", direction: "asc" };

function matches(entity: EntitySummary, filters: CompanyFilters): boolean {
  const q = filters.q.trim().toLowerCase();
  if (q) {
    const haystack = `${entity.legalName} ${entity.code} ${entity.crNumber} ${entity.participantId}`.toLowerCase();
    if (!haystack.includes(q)) return false;
  }
  if (filters.status && entity.status !== filters.status) return false;
  if (filters.health && entity.health !== filters.health) return false;
  if (filters.method && entity.connectionMethod !== filters.method) return false;
  if (filters.wave && entity.wave !== filters.wave) return false;
  return true;
}

/** Missing timestamps sort as the oldest, in both directions. */
function compareValue(entity: EntitySummary, key: SortKey): string | number {
  switch (key) {
    case "lastDocumentSeenAt":
      return entity.lastDocumentSeenAt ? new Date(entity.lastDocumentSeenAt).getTime() : 0;
    case "legalName":
      return entity.legalName;
    case "status":
      return entity.status;
    case "health":
      return entity.health;
  }
}

function sortEntities(items: EntitySummary[], sort: SortState): EntitySummary[] {
  const sorted = [...items].sort((a, b) => {
    const av = compareValue(a, sort.key);
    const bv = compareValue(b, sort.key);
    if (av < bv) return -1;
    if (av > bv) return 1;
    return 0;
  });
  if (sort.direction === "desc") sorted.reverse();
  return sorted;
}

function SortHeader({
  label,
  sortKey,
  sort,
  onSort,
  className,
}: {
  label: string;
  sortKey: SortKey;
  sort: SortState;
  onSort: (key: SortKey) => void;
  className?: string;
}) {
  const active = sort.key === sortKey;
  const ariaSort = active ? (sort.direction === "asc" ? "ascending" : "descending") : "none";
  return (
    <th className={className} aria-sort={ariaSort}>
      <button type="button" className={styles.sortBtn} onClick={() => onSort(sortKey)}>
        {label}
        {active ? <span aria-hidden>{sort.direction === "asc" ? " ▲" : " ▼"}</span> : null}
      </button>
    </th>
  );
}

export function CompaniesList() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const entities = useEntities();
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);

  const filters: CompanyFilters = useMemo(
    () => ({
      q: searchParams.get("q") ?? "",
      status: searchParams.get("status") ?? "",
      health: searchParams.get("health") ?? "",
      method: searchParams.get("method") ?? "",
      wave: searchParams.get("wave") ?? "",
    }),
    [searchParams],
  );

  function patchFilters(patch: Partial<CompanyFilters>) {
    const merged = { ...filters, ...patch };
    const next = new URLSearchParams();
    for (const key of FILTER_KEYS) {
      const value = merged[key];
      if (value) next.set(key, value);
    }
    setSearchParams(next, { replace: true });
  }

  function clearFilters() {
    setSearchParams(new URLSearchParams(), { replace: true });
  }

  function onSort(key: SortKey) {
    setSort((prev) =>
      prev.key === key ? { key, direction: prev.direction === "asc" ? "desc" : "asc" } : { key, direction: "asc" },
    );
  }

  const all = useMemo(() => entities.data ?? [], [entities.data]);
  const waves = useMemo(
    () => Array.from(new Set(all.map((entity) => entity.wave).filter((wave): wave is string => Boolean(wave)))).sort(),
    [all],
  );
  const filtered = useMemo(() => sortEntities(all.filter((entity) => matches(entity, filters)), sort), [all, filters, sort]);

  return (
    <>
      <div className="page-head">
        <div className="grow">
          <h1 className="display">{t("companies.list.title", "Companies")}</h1>
          <p className={styles.lede}>{t("companies.list.lede", "Every company in the group, in one place.")}</p>
        </div>
      </div>

      <section className="card card-tight">
        <div className="card-head wrap">
          <CompaniesFilters filters={filters} waves={waves} onChange={patchFilters} onClear={clearFilters} />
        </div>

        {entities.isError ? (
          <div className="card-body">
            <Notice kind="fail">{t("companies.list.loadError", "The company list could not be loaded.")}</Notice>
          </div>
        ) : all.length === 0 ? (
          <div className="card-body">
            <Notice kind="info">
              <div className="row">
                <span className="grow">{t("companies.list.empty", "No companies yet")}</span>
                <span
                  className={styles.disabledAction}
                  aria-disabled="true"
                  title={t("companies.list.onboardingSoon", "Onboarding ships in the next release")}
                >
                  {t("companies.list.onboardingSoon", "Onboarding ships in the next release")}
                </span>
              </div>
            </Notice>
          </div>
        ) : filtered.length === 0 ? (
          <div className="card-body">
            <Notice kind="info">
              <div className="row">
                <span className="grow">{t("companies.list.noMatches", "No companies match these filters")}</span>
                <button type="button" className="btn btn-sm btn-ghost" onClick={clearFilters}>
                  {t("companies.list.clearFilters", "Clear filters")}
                </button>
              </div>
            </Notice>
          </div>
        ) : (
          <div className={`table-wrap ${styles.tableScroll}`}>
            <table className="tbl">
              <thead>
                <tr>
                  <SortHeader
                    label={t("companies.list.columns.company", "Company")}
                    sortKey="legalName"
                    sort={sort}
                    onSort={onSort}
                  />
                  <th>{t("companies.list.columns.cr", "CR")}</th>
                  <th>{t("companies.list.columns.participantId", "Participant ID")}</th>
                  <th>{t("companies.list.columns.erp", "ERP")}</th>
                  <th className="nowrap">{t("companies.list.columns.method", "Method")}</th>
                  <SortHeader
                    label={t("companies.list.columns.status", "Status")}
                    sortKey="status"
                    sort={sort}
                    onSort={onSort}
                  />
                  <SortHeader
                    label={t("companies.list.columns.health", "Health")}
                    sortKey="health"
                    sort={sort}
                    onSort={onSort}
                  />
                  <th className="nowrap">{t("companies.list.columns.mappingCoverage", "Mapping coverage")}</th>
                  <SortHeader
                    label={t("companies.list.columns.lastDocument", "Last document")}
                    sortKey="lastDocumentSeenAt"
                    sort={sort}
                    onSort={onSort}
                    className="nowrap"
                  />
                </tr>
              </thead>
              <tbody>
                {filtered.map((entity) => (
                  <tr key={entity.id}>
                    <td>
                      <Link className="link" to={`/companies/${entity.id}`}>
                        <CompanyChip entityId={entity.id} name={entity.legalName} code={entity.code} />
                      </Link>
                    </td>
                    <td className="mono nowrap">
                      <span className="row-tight">
                        {entity.crNumber}
                        <CopyButton
                          value={entity.crNumber}
                          label={t("companies.list.copyCrLabel", "Copy CR number for {{name}}", {
                            name: entity.legalName,
                          })}
                        />
                      </span>
                    </td>
                    <td className="mono nowrap">
                      <span className="row-tight">
                        {entity.participantId}
                        <CopyButton
                          value={entity.participantId}
                          label={t("companies.list.copyParticipantLabel", "Copy participant ID for {{name}}", {
                            name: entity.legalName,
                          })}
                        />
                      </span>
                    </td>
                    <td className="nowrap">
                      {entity.erpType}
                      {entity.erpVersion ? ` ${entity.erpVersion}` : ""}
                    </td>
                    <td className="nowrap">
                      <span title={METHODS[METHOD_BY_CONNECTION[entity.connectionMethod]].full}>
                        <MethodChip method={METHOD_BY_CONNECTION[entity.connectionMethod]} />
                      </span>
                    </td>
                    <td>
                      <StatusPill status={entity.status} label={STATUS_LABEL[entity.status]} />
                    </td>
                    <td>
                      <StatusPill {...HEALTH_PILL[entity.health]} />
                    </td>
                    <td>
                      <Coverage mappingCoverage={entity.mappingCoverage} />
                    </td>
                    <td className="nowrap">{entity.lastDocumentSeenAt ? relative(entity.lastDocumentSeenAt) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
